import { useEffect, useState } from 'react';
import {
  collection, query, orderBy, onSnapshot, getDocs,
  addDoc, updateDoc, deleteDoc, doc, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_TASKS = [
  // Logistics
  { title: 'Flights booked',                category: 'Logistics' },
  { title: 'Hotel confirmed',               category: 'Logistics' },
  { title: 'Airport transfers arranged',    category: 'Logistics' },
  { title: 'Match / fixture confirmed',     category: 'Logistics' },
  { title: 'Kit delivery confirmed',        category: 'Logistics' },
  // Compliance
  { title: 'Travel insurance arranged',     category: 'Compliance' },
  { title: 'Visa / passport requirements checked', category: 'Compliance' },
  { title: 'Risk assessment completed',     category: 'Compliance' },
  // Admin
  { title: 'Participant list received',     category: 'Admin' },
  { title: 'Emergency contacts collected',  category: 'Admin' },
  { title: 'Deposit received',              category: 'Admin' },
  { title: 'Final balance received',        category: 'Admin' },
  { title: 'Documents sent to customer',   category: 'Admin' },
];

const CATEGORY_ORDER = ['Logistics', 'Compliance', 'Admin', 'Custom'];

export default function TaskChecklist({ requestId }) {
  const { currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, 'requests', requestId, 'tasks'),
      orderBy('order', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [requestId]);

  async function loadDefaults() {
    // Try to load from Firestore checklistTemplates (active only); fall back to hardcoded list
    let taskDefs = [];
    try {
      const snap = await getDocs(query(collection(db, 'checklistTemplates'), orderBy('order', 'asc')));
      const active = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => t.active !== false);
      if (active.length > 0) {
        taskDefs = active.map((t) => ({ title: t.name, category: t.category || 'Ops' }));
      }
    } catch (_) { /* ignore — use fallback */ }

    if (taskDefs.length === 0) {
      taskDefs = DEFAULT_TASKS;
    }

    const batch = writeBatch(db);
    taskDefs.forEach((t, i) => {
      const ref = doc(collection(db, 'requests', requestId, 'tasks'));
      batch.set(ref, {
        title: t.title,
        category: t.category,
        completed: false,
        completedByEmail: null,
        completedAt: null,
        createdAt: serverTimestamp(),
        order: i,
      });
    });
    await batch.commit();
  }

  async function toggleTask(task) {
    const ref = doc(db, 'requests', requestId, 'tasks', task.id);
    if (task.completed) {
      await updateDoc(ref, {
        completed: false,
        completedByEmail: null,
        completedAt: null,
      });
    } else {
      await updateDoc(ref, {
        completed: true,
        completedByEmail: currentUser.email,
        completedAt: serverTimestamp(),
      });
    }
  }

  async function addTask(e) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      await addDoc(collection(db, 'requests', requestId, 'tasks'), {
        title: newTitle.trim(),
        category: 'Custom',
        completed: false,
        completedByEmail: null,
        completedAt: null,
        createdAt: serverTimestamp(),
        order: tasks.length,
      });
      setNewTitle('');
      setShowInput(false);
    } finally {
      setAdding(false);
    }
  }

  async function deleteTask(taskId) {
    await deleteDoc(doc(db, 'requests', requestId, 'tasks', taskId));
  }

  const completed = tasks.filter((t) => t.completed).length;
  const total = tasks.length;
  const progress = total === 0 ? 0 : Math.round((completed / total) * 100);

  // Group by category — include all categories found in tasks, ordered by CATEGORY_ORDER first
  const allCats = [
    ...CATEGORY_ORDER,
    ...tasks.map((t) => t.category || 'Other').filter((c) => !CATEGORY_ORDER.includes(c)),
  ].filter((c, i, arr) => arr.indexOf(c) === i); // deduplicate

  const grouped = allCats.reduce((acc, cat) => {
    const items = tasks.filter((t) => (t.category || 'Other') === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Ops Checklist</h3>
          {total > 0 && (
            <p className="text-[12px] text-[#9CA3AF] mt-0.5">{completed} of {total} complete</p>
          )}
        </div>
        {total > 0 && (
          <span className={`text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] ${
            progress === 100 ? 'bg-green-light text-[#065F46]' : 'bg-[#F3F4F6] text-[#6B7280]'
          }`}>
            {progress}%
          </span>
        )}
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div className="w-full bg-[#E5E7EB] rounded-full h-1.5 mb-5">
          <div
            className="bg-pb h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : total === 0 ? (
        <div className="text-center py-4">
          <p className="text-[13px] text-[#9CA3AF] mb-3">No tasks yet.</p>
          <button
            onClick={loadDefaults}
            className="text-[13px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition"
          >
            Load default tasks
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category}>
              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-2">{category}</p>
              <div className="space-y-1">
                {items.map((task) => (
                  <div key={task.id} className="flex items-start gap-3 group py-1">
                    <button
                      onClick={() => toggleTask(task)}
                      className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition ${
                        task.completed
                          ? 'bg-pb border-pb'
                          : 'border-[#D1D5DB] hover:border-pb'
                      }`}
                    >
                      {task.completed && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 8">
                          <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] ${task.completed ? 'line-through text-[#9CA3AF]' : 'text-[#111827]'}`}>
                        {task.title}
                      </p>
                      {task.completed && task.completedByEmail && (
                        <p className="text-[12px] text-[#9CA3AF] mt-0.5">
                          {task.completedByEmail}
                        </p>
                      )}
                    </div>
                    {category === 'Custom' && (
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="opacity-0 group-hover:opacity-100 text-[#D1D5DB] hover:text-dr transition text-[12px]"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add custom task */}
      <div className="mt-5 pt-4 border-t border-[#F3F4F6]">
        {showInput ? (
          <form onSubmit={addTask} className="flex gap-2">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Task name…"
              autoFocus
              className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
            <button
              type="submit"
              disabled={adding || !newTitle.trim()}
              className="px-3 py-1.5 text-[13px] font-medium text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => { setShowInput(false); setNewTitle(''); }}
              className="px-3 py-1.5 text-[13px] text-[#6B7280] hover:text-[#374151]"
            >
              Cancel
            </button>
          </form>
        ) : (
          <button
            onClick={() => setShowInput(true)}
            className="text-[13px] text-[#9CA3AF] hover:text-pb font-medium transition"
          >
            + Add task
          </button>
        )}
      </div>
    </div>
  );
}
